import type { QuestionnaireVersion } from './types.js';

/**
 * Version 1 of the Service 1 questionnaire.
 *
 * The words are the client's, from the intake form they supplied, with three changes and no others.
 * Each is recorded here because a change to someone else's content has to be defensible:
 *
 * 1. The second section's heading became "The focus of your reflection". The original used an
 *    intake-form term borrowed from practice, which law 1 forbids and the check refuses — it is
 *    not reproduced here for that reason. The question it asks is unchanged.
 *
 * 2. "children (including names, DOB, and abortions)" became its own group with two separate,
 *    optional questions, the second worded "pregnancies that did not continue". Question 6 in
 *    docs/14 requires that this answer be optional, neutrally worded, separately consented, and
 *    encrypted at field level — none of which is possible while it is one clause inside a larger
 *    free-text box.
 *
 * 3. The single "tell us about you" box became eight fields. Same information, asked one thing at a
 *    time, so the sensitive part can carry its own gate and its own encryption while a name and a
 *    profession do not have to.
 *
 * Anything beyond these is not mine to make. A new question, a reworded prompt, a changed required
 * flag — all of it is a **YOU** task and stays blocked until it arrives in writing.
 */
export const SERVICE_1_QUESTIONNAIRE_V1: QuestionnaireVersion = {
  slug: 'remote-chart',
  version: 1,
  locale: 'en',

  welcome: [
    'Welcome.',
    'Please complete this form as honestly and fully as you are called to.',
    'This intake will help create your personalized Tooth Constellation Chart and, if you choose a live session, guide a deeper exploration of your family system and recurring life themes.',
    'As you move through this process, please be aware that certain memories, emotions, realizations, or family dynamics may begin to surface. Tooth Somatics can bring awareness to places in your life that feel tender, uncomfortable, or confronting. This is not something to fear, but something to approach with honesty and gentleness. Often, the places that challenge us are also the places where growth, integration, and liberation can begin.',
    'This process is educational and reflective. It does not replace dental, medical, psychological, or psychiatric care.',
  ],

  groups: [
    {
      key: 'about_you',
      title: 'About you',
      intro: 'A few details, one at a time. Only your name and date of birth are needed.',
      questions: [
        {
          key: 'full_name',
          label: 'Your name',
          type: 'short_text',
          required: true,
          maxLength: 200,
        },
        { key: 'date_of_birth', label: 'Your date of birth', type: 'date', required: true },
        {
          key: 'place_of_birth',
          label: 'Where you were born',
          type: 'short_text',
          required: false,
          maxLength: 200,
        },
        {
          key: 'current_home',
          label: 'Where you live now',
          type: 'short_text',
          required: false,
          maxLength: 200,
        },
        {
          key: 'profession',
          label: 'Your profession, and what your current work is',
          type: 'long_text',
          required: false,
          maxLength: 2000,
        },
        {
          key: 'relationship_status',
          label: 'Your relationship status',
          type: 'short_text',
          required: false,
          maxLength: 200,
        },
      ],
    },

    {
      key: 'family',
      title: 'Children',
      intro:
        'Both questions here are optional, and you may answer one and leave the other. Anything you write is stored separately from the rest of your answers and is never included in an export unless you ask for it.',
      // Enforced by the API before a single answer in this group is stored.
      gate: 'sensitive_information_optional',
      questions: [
        {
          key: 'children',
          label: 'Your children — names and dates of birth',
          type: 'long_text',
          required: false,
          sensitive: true,
          maxLength: 4000,
        },
        {
          key: 'pregnancies_not_continued',
          label: 'Pregnancies that did not continue',
          help: 'Include as much or as little as you wish. Leaving this blank changes nothing about your reflection.',
          type: 'long_text',
          required: false,
          sensitive: true,
          maxLength: 4000,
        },
      ],
    },

    {
      key: 'focus',
      title: 'The focus of your reflection',
      intro: 'One topic, chosen by you. This is what the reflection is built around.',
      questions: [
        {
          key: 'focus_topic',
          label: 'The one topic you would like to explore',
          help: 'This may be a recurring emotional pattern, a relationship dynamic, a family theme, or a particular position or area of your mouth that feels unresolved or important to you.',
          type: 'long_text',
          required: true,
          maxLength: 8000,
        },
      ],
    },
  ],
};
